import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import S from './Connexion.module.scss';
import * as s from '../../ressources/StyleGlobal/StyleGlobal';
import Bouton from '../UI/Bouton/Bouton';
import * as actions from '../../services/actions';
import FondGris from '../UI/FondGris/FondGris';
import FondBlanc from '../UI/FondBlanc/FondBlanc';
import * as requestType from '../../ressources/glossaires/glossaire_requetes';
import AttenteRequete from '../../hoc/AttenteRequete/AttenteRequete';
import * as url from '../../ressources/glossaires/glossaire_liens_pages';
import * as privilege from '../../ressources/glossaires/glossaire_privileges';
import logoEntry from '../../ressources/Logo/argos_png_logo_white.png';
import CommutateurLangue from '../CommutateurLangue/CommutateurLangue';
import { checkMot } from '../../ressources/functionJS/checkMot';

const Connexion = () => {
  const dispatch = useDispatch();

  // States from Redux
  const estAuth = useSelector((state) => state.authentifie.identifier);
  const premiereConnexion = useSelector(
    (state) => state.authentifie.reConnexion
  );
  const reduxLoginName = useSelector((state) => state.authentifie.login_name);
  const demandeReinitialisationMDPBool = useSelector(
    (state) => state.authentifie.demandeReinitialisationMDPReussie
  );
  const listePrivilege = useSelector(
    (state) => state.authentifie.privileges_list
  );
  const alertesListe = useSelector((state) => state.alrt.alertesListe);

  // Local states
  const [loginName, setLoginName] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [afficheReinitialisationBool, setAfficheReinitialisationBool] =
    useState(false);
  const [hasPasswordError, setHasPasswordError] = useState(false);
  const [hasPasswordSuccess, setHasPasswordSuccess] = useState(false);
  const [hasEmailError, setHasEmailError] = useState(false);
  const [hasEmailNotFound, setHasEmailNotFound] = useState(false);

  // Fonction de validation d'email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Vérifier si le formulaire est valide
  const isFormValid = () => {
    const email = reduxLoginName || loginName;
    return (
      email &&
      motDePasse &&
      validateEmail(email) &&
      !hasEmailError &&
      !hasEmailNotFound &&
      !hasPasswordError
    );
  };

  useEffect(() => {
    // Initial connection info request
    dispatch(actions.demandeInformationDeConnexion);

    // Handle default admin menu display based on URL
    if (listePrivilege.indexOf(privilege.acces_a_l_interface_admin) >= 0) {
      const urlDeLaPage = window.location.href.split('/')[3];
      const adminUrls = [
        url.projet,
        url.filtre,
        url.capteur,
        url.graphe,
        url.vue,
        url.alarme,
        url.rapport,
        url.utilisateur,
        url.tasks,
        url.maintenance,
        url.AideAdmin,
        url.planning,
      ];

      if (adminUrls.includes('/' + urlDeLaPage)) {
        dispatch(actions.commutateurInterfaceAdmin());
      }
    }
  }, [dispatch, listePrivilege]);

  // Détecter les erreurs de connexion dans les alertes
  useEffect(() => {
    const hasLoginErrorAlert = alertesListe.some(
      (alerte) =>
        alerte.corps === 'Email incorrect ou mot de passe incorrect.' ||
        alerte.corps === 'Email incorrect ou mot de passe incorrect'
    );

    // Quand il y a une erreur de connexion, les deux champs doivent être en rouge
    setHasPasswordError(hasLoginErrorAlert);
    setHasEmailError(hasLoginErrorAlert);
    setHasEmailNotFound(hasLoginErrorAlert);
  }, [alertesListe]);

  // Détecter le succès de connexion
  useEffect(() => {
    if (estAuth && motDePasse) {
      setHasPasswordSuccess(true);
      // Réinitialiser après 2 secondes
      const timer = setTimeout(() => {
        setHasPasswordSuccess(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [estAuth, motDePasse]);

  // Détecter l'auto-remplissage des champs par le navigateur
  useEffect(() => {
    const checkAutoFill = () => {
      const emailInput = document.getElementById('staticEmail');
      const passwordInput = document.getElementById('inputPassword');

      if (emailInput && emailInput.value && emailInput.value !== loginName) {
        setLoginName(emailInput.value);
      }

      if (
        passwordInput &&
        passwordInput.value &&
        passwordInput.value !== motDePasse
      ) {
        setMotDePasse(passwordInput.value);
      }
    };

    // Vérifier périodiquement pendant les premières secondes
    const intervals = [100, 500, 1000, 2000].map((delay) =>
      setTimeout(checkAutoFill, delay)
    );

    return () => {
      intervals.forEach(clearTimeout);
    };
  }, [loginName, motDePasse]);

  const handleEmailChange = (event) => {
    const email = event.target.value;
    setLoginName(email);

    // Réinitialiser tous les états d'erreur de connexion quand l'utilisateur tape
    if (hasEmailError || hasEmailNotFound || hasPasswordError) {
      setHasEmailError(false);
      setHasEmailNotFound(false);
      setHasPasswordError(false);
    }

    // Valider l'email en temps réel si ce n'est pas vide
    if (email && !validateEmail(email)) {
      setHasEmailError(true);
    }
  };

  const handlePasswordChange = (event) => {
    setMotDePasse(event.target.value);
    // Réinitialiser tous les états d'erreur de connexion quand l'utilisateur tape
    if (hasPasswordError || hasEmailError || hasEmailNotFound) {
      setHasPasswordError(false);
      setHasEmailError(false);
      setHasEmailNotFound(false);
    }
    if (hasPasswordSuccess) {
      setHasPasswordSuccess(false);
    }
  };
  const toggleReinitialisation = () =>
    setAfficheReinitialisationBool((prev) => !prev);

  const handleLogin = () => {
    const email = reduxLoginName || loginName;

    // Valider l'email avant d'envoyer la requête
    if (!validateEmail(email)) {
      setHasEmailError(true);
      setHasPasswordError(true); // Les deux champs en rouge pour erreur de validation
      dispatch(
        actions.ajouterAlerte(
          'danger',
          'Email incorrect ou mot de passe incorrect.',
          'Email incorrect ou mot de passe incorrect.'
        )
      );
      return;
    }

    dispatch(actions.authentification(email, motDePasse));
  };

  const handleResetPassword = (email) => {
    dispatch(actions.demandeReinitialisationMDP(email));
  };

  // Determine email and classes
  const emailClass = reduxLoginName
    ? [s.form_control_plaintext, S.IdUtlisateur].join(' ')
    : [
        s.form_control,
        S.IdUtlisateur,
        hasEmailError || hasEmailNotFound ? S.EmailErreur : '',
      ].join(' ');
  const email = reduxLoginName || loginName;
  const classeConnexion = premiereConnexion
    ? S.Connexion
    : S.PageAccueilConnexion;

  const renderLoginForm = () => (
    <>
      <div className={S.divCommutateurLangue}>
        <CommutateurLangue typeAffichage={'bouton'} />
      </div>
      <div className={S.Text}>
        <img alt="logo" src={logoEntry} />
      </div>
      <form
        className={S.FormulaireConnexion}
        onSubmit={(e) => e.preventDefault()}
      >
        <div className={[s.form_group, s.row].join(' ')}>
          <label
            htmlFor="staticEmail"
            className={[s.col, s.col_form_label].join(' ')}
          >
            {checkMot('email')}
          </label>
          <div className={[s.col_sm_12].join(' ')}>
            <input
              type="text"
              className={emailClass}
              id="staticEmail"
              readOnly={!!reduxLoginName}
              placeholder="emailExemple"
              value={email}
              onChange={handleEmailChange}
            />
          </div>
        </div>
        <div className={[s.form_group, s.row].join(' ')}>
          <label
            htmlFor="inputPassword"
            className={[s.col, s.col_form_label].join(' ')}
          >
            {checkMot('mot_de_passe')}
          </label>
          <div className={[s.col_sm_12].join(' ')}>
            <input
              type="password"
              className={[
                s.form_control,
                S.MotDePasse,
                hasPasswordError ? S.MotDePasseErreur : '',
                hasPasswordSuccess ? S.MotDePasseSucces : '',
              ].join(' ')}
              id="inputPassword"
              placeholder="motDePasse"
              value={motDePasse}
              onChange={handlePasswordChange}
            />
          </div>
        </div>
        <div className={S.Bouton}>
          <AttenteRequete
            request_type={requestType.information_de_connexion}
            str={checkMot('re_connexion_en_cour')}
          >
            <AttenteRequete
              request_type={requestType.connexion}
              str={checkMot('connexion_en_cour')}
            >
              <Bouton cliquer={handleLogin} desactive={!isFormValid()}>
                {checkMot('connexion')}
              </Bouton>
            </AttenteRequete>
          </AttenteRequete>
        </div>
      </form>
      <div className={S.LienReinitialisation}>
        <Link to={''} onClick={toggleReinitialisation}>
          {checkMot('demande_de_reinitialise_de_mdp')}
        </Link>
      </div>
    </>
  );

  const renderResetForm = () => (
    <>
      <div className={S.Text}>
        <img alt="logo" src={logoEntry} />
      </div>
      <div className={S.FormulaireReinitialisation}>
        <h4 className={S.TextTitre}>
          {checkMot('demande_de_reinitialise_de_mdp')}
        </h4>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className={[s.form_group, s.row].join(' ')}>
            <label
              htmlFor="resetEmail"
              className={[s.col_sm_2, s.col_form_label].join(' ')}
            >
              {checkMot('email')}:
            </label>
            <input
              type="text"
              className={[s.form_control, S.IdUtlisateur, emailClass].join(' ')}
              id="resetEmail"
              readOnly={!!reduxLoginName}
              placeholder="emailExemple"
              value={email}
              onChange={handleEmailChange}
            />
          </div>
          <div className={S.Bouton}>
            <AttenteRequete
              request_type={
                requestType.demande_de_reinitialisation_du_mot_de_passe
              }
              str={checkMot('demande_en_cour')}
            >
              <Bouton
                className={[s.btn, s.btn_primary].join(' ')}
                cliquer={() => handleResetPassword(email)}
              >
                {checkMot('envoyer')}
              </Bouton>
            </AttenteRequete>
          </div>
        </form>
        <Bouton
          ajoutClass={S.CommutateurReinitialisation}
          cliquer={toggleReinitialisation}
        >
          <svg
            width="1em"
            height="1em"
            viewBox="0 0 16 16"
            className="bi bi-arrow-bar-left"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M12.5 15a.5.5 0 0 1-.5-.5v-13a.5.5 0 0 1 1 0v13a.5.5 0 0 1-.5.5zM10 8a.5.5 0 0 1-.5.5H3.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L3.707 7.5H9.5a.5.5 0 0 1 .5.5z"
            />
          </svg>
        </Bouton>
      </div>
    </>
  );

  const renderResetSuccess = () => (
    <>
      <div className={S.Text}>
        <img alt="logo" src={logoEntry} />
      </div>
      <div className={S.FormulaireReinitialisation}>
        <h4 className={S.TextTitre}>
          {checkMot('un_lien_de_reinitialisation_vous_envoye_adresse')} {email}.
        </h4>
        <Bouton
          ajoutClass={S.CommutateurReinitialisation}
          cliquer={toggleReinitialisation}
        >
          <svg
            width="1em"
            height="1em"
            viewBox="0 0 16 16"
            className="bi bi-arrow-bar-left"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M12.5 15a.5.5 0 0 1-.5-.5v-13a.5.5 0 0 1 1 0v13a.5.5 0 0 1-.5.5zM10 8a.5.5 0 0 1-.5.5H3.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L3.707 7.5H9.5a.5.5 0 0 1 .5.5z"
            />
          </svg>
        </Bouton>
      </div>
    </>
  );

  if (estAuth) return null;

  return (
    <>
      {premiereConnexion ? <FondGris /> : <FondBlanc />}
      <div className={classeConnexion}>
        {afficheReinitialisationBool ? (
          demandeReinitialisationMDPBool ? (
            renderResetSuccess()
          ) : (
            renderResetForm()
          )
        ) : (
          <AttenteRequete
            request_type={requestType.information_de_connexion}
            str=""
          >
            {renderLoginForm()}
          </AttenteRequete>
        )}
      </div>
    </>
  );
};

export default Connexion;
